import React, { Component } from 'react'
import { Switch, Route } from 'react-router-dom'
import { ConnectedRouter } from 'react-router-redux'
import { parse } from 'query-string'
import { Icon } from '@drifthq/tide-core'

import { hasSegmentSnippet, installSegment, installWidgetStub } from '@drifthq/traffic-control'
import { getAccessToken, setAccessToken, deleteAccessToken } from '@drifthq/web-auth'
import Cookies from 'js-cookie'

import Setup from 'layouts/Setup'
import Deal from 'layouts/Deal'
import DealExpired from 'layouts/DealExpired'
import DealError from 'layouts/DealError'
import DealPending from 'layouts/DealPending'
import DealUpgradeSuccessStep from 'layouts/DealUpgradeSuccessStep'
import DealOnboardingFlow from 'flows/DealOnboardingFlow'
import OnboardingFlow from 'flows/OnboardingFlow';
import TouchlessStart from 'layouts/TouchlessStart'
import VideoController from 'layouts/VideoController'

import Billing from 'layouts/Billing'

import { Flow } from 'constants/flowConstants'
import { DealCodeCookie } from 'constants/dealConstants'

import LoadingPage from 'layouts/LoadingPage'
import { fetchOnboardingStatus } from 'modules/state/api'

import './style.css'

const connectWithRouter = (WrappedComponent) => (props) => {
  const { history, ...rest } = props
  return (
    <ConnectedRouter history={history} {...rest}>
      <WrappedComponent {...props} />
    </ConnectedRouter>
  )
}

const InviteError = () =>  (
  <div className="app-loading-placeholder">
    <Icon name="drift-logo-round" width={100} height={100} className="start-drift-logo"/>
    <h2>Something went wrong</h2>
    <h4>Please have your administrator generate a new invitation</h4>
  </div>
)

class App extends Component {

  state = {
    loading: false
  }

  async componentWillMount() {
    const { history, actions, isGdprConsentRequired } = this.props

    if (!hasSegmentSnippet()) {
      installWidgetStub()
      installSegment()
    }

    const {
      email,
      resetToken,
      token,
      sso,
      dealCode,
      flow = Flow.WIDGET,
      isCalendaringSeat,
      thirdPartySignup,
      plan,
      coupon,
      state,
      redirect_uri,
    } = parse(history.location.search)

    if (redirect_uri) {
      localStorage.setItem('hyfy_redirect_uri', redirect_uri)
      localStorage.setItem('hyfy_state', state)
    }

    if (plan) {
      this.props.actions.setPlan(plan)
    }

    if (coupon) {
      this.props.actions.setCoupon(coupon)
    }

    if (resetToken) {
      actions.setFlow(flow)
      this.redirectActivatedUserToSetup({ flow, token: resetToken, isCalendaringSeat })
    } else if (token && thirdPartySignup) {
      setAccessToken({ token: token})
      this.redirectThirdPartyUserToSetup()
    } else if (token) {
      setAccessToken({token: token})
    } else if (sso) {
      this.redirectSsoUserToSetup({ email, isCalendaringSeat })
    } else if (dealCode) {
      this.redirectDealCode(dealCode)
    } else if (isGdprConsentRequired) {
      if (email) {
        deleteAccessToken()
        actions.setEmail(email)
      }
      return this.redirectSignup()
    } else if (email) {
      deleteAccessToken()
      actions.setFlow(flow)
      actions.setEmail(email)
      actions.setDisabledEmail(true)
      actions.signup({ email })
      return this.redirectSignup()
    } else if (history.location.pathname.includes('video')) {
      actions.setFlow(Flow.VIDEO)
    } else {
      const lastOnboardingStepLocation = await this.fetchLastOnboardingStep()
      if (lastOnboardingStepLocation) {
        history.push(lastOnboardingStepLocation)
      } else if (history.location.pathname === '/') {
        this.redirectRoot()
      }
    }
  }

  fetchLastOnboardingStep = async () => {
    this.setState({ loading: true })

    try {
      const response = await fetchOnboardingStatus()
      if (!response.isComplete && !!response.lastLocation) {
        return response.lastLocation
      }
    } catch (err) {
      console.warn(err)
    } finally {
      this.setState({ loading: false })
    }

    return null
  }

  redirectActivatedUserToSetup = ({ flow, token, isCalendaringSeat }) => {
    const { history, actions } = this.props

    deleteAccessToken()
    actions.setResetToken(token)
    if (flow === Flow.VIDEO) {
      history.push('/setup/password')
    } else if (isCalendaringSeat) {
      history.push('/setup/calendaring/activate')
    } else {
      history.push(`/setup`)
    }
  }

  redirectSsoUserToSetup = ({ email, isCalendaringSeat }) => {
    const { history, actions } = this.props

    if(getAccessToken()) {
      actions.fetchUser()
      actions.setSSOProvisionedFlow(true)
      if (isCalendaringSeat) {
        history.push('/setup/calendaring/activate')
      } else {
        history.push(`/setup`)
      }
    } else {
      actions.setEmail(email)
      actions.getLoginUrl()
    }
  }

  redirectThirdPartyUserToSetup = () => {
    const { history } = this.props
    history.push('/setup/touchless/custom')
  }

  redirectDealCode = (dealCode) => {
    const { history } = this.props

    if (dealCode) {
      Cookies.set(DealCodeCookie, dealCode)
      history.push(`/deal`)
    }
  }

  redirectRoot = () => {
    const { history } = this.props
    history.push({ pathname: '/start', search: history.location.search })
  }

  redirectSignup = () => {
    const { history } = this.props
    history.push({ pathname: '/start/signup', search: history.location.search })
  }

  componentDidUpdate(prevProps) {
    if(!prevProps.ssoRedirectError && this.props.ssoRedirectError) {
      this.props.history.push(`/invite-error`)
    }
  }

  render() {
    if (this.state.loading) {
      return <LoadingPage/>
    }

    return (
      <Switch>
        <Route path="/setup" component={Setup}/>
        <Route path="/video" component={VideoController}/>
        <Route path="/start" component={TouchlessStart}/>
        <Route path="/deal" component={Deal}/>
        <Route path="/deal-expired" component={DealExpired}/>
        <Route path="/deal-error" component={DealError}/>
        <Route path="/deal-pending" component={DealPending}/>
        <Route path="/deal-upgraded" component={DealUpgradeSuccessStep}/>
        <Route path="/invite-error" component={InviteError}/>
        <Route path="/deal-onboarding" component={DealOnboardingFlow}/>
        <Route path="/onboarding" component={OnboardingFlow}/>
        <Route path="/billing" component={Billing}/>
        <Route path="/" component={LoadingPage}/>
      </Switch>
    )
  }
}

export default connectWithRouter(App)



// WEBPACK FOOTER //
// ./src/layouts/App/component.js